import React, { useState } from 'react';

function EmailHistoryModal({ lead, category, onClose }) {
  const [expandedEmail, setExpandedEmail] = useState(null);

  const getCategoryColor = (cat) => {
    switch (cat) {
      case 'Promotional': return 'bg-green-100 text-green-800 border-green-200';
      case 'Follow-up': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Newsletter': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return 'No content';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const filteredCampaigns = lead.emailCampaigns?.filter((campaign) => 
    category ? campaign.category === category : true
  ) || [];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">Email Campaign History</h2>
              <p className="text-green-100 mt-1">
                {lead.schoolName} {category && `- ${category} Emails`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {filteredCampaigns.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Email Campaigns</h3>
              <p className="text-gray-500">
                {category 
                  ? `No ${category.toLowerCase()} emails have been sent to this lead yet.`
                  : 'No email campaigns have been sent to this lead yet.'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCampaigns
                .sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt))
                .map((campaign, index) => (
                <div key={campaign._id || index} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                    {/* Email Info */}
                    <div className="flex-shrink-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getCategoryColor(campaign.category)}`}>
                          {campaign.category}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="font-medium">{campaign.sentBy?.username || 'Unknown User'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span>{formatDate(campaign.sentAt)}</span>
                      </div>
                    </div>

                    {/* Email Content */}
                    <div className="flex-1">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Email Content
                        </h4>
                        <div className="text-gray-700 leading-relaxed">
                          {expandedEmail === campaign._id ? (
                            <div>
                              <p className="whitespace-pre-wrap">{campaign.content || 'No content available'}</p>
                              <button
                                onClick={() => setExpandedEmail(null)}
                                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200"
                              >
                                Show Less
                              </button>
                            </div>
                          ) : (
                            <div>
                              <p className="whitespace-pre-wrap">{truncateText(campaign.content)}</p>
                              {campaign.content && campaign.content.length > 100 && (
                                <button
                                  onClick={() => setExpandedEmail(campaign._id)}
                                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium transition-colors duration-200"
                                >
                                  Show More
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {filteredCampaigns.length} email{filteredCampaigns.length !== 1 ? 's' : ''} found
            </div>
            <button
              onClick={onClose}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailHistoryModal;